import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import CommonPopup from "Components/common/CommonPopup";
import NumberInputBox from "Components/common/NumberInputBox";
import QuilEditor from "Components/common/quillEditor";
import Button from "Components/common/Button";
import connectedInboxStore from "./store";

const FIELD_KEYS = [
  "fromName",
  "senderName",
  "calendarUrl",
  "dailyLimit",
  "interval",
  "signature"
];
const NUMBER_FIELD_KEYS = ["dailyLimit", "interval"];
const CHIP_LIMIT = 6;

const getInboxEmail = (inbox = {}) =>
  (inbox?.type === "smtp" ? inbox?.smtp?.email : inbox?.email) || "";

const resolveInitialValues = (inboxes = []) => {
  const initial = {};
  FIELD_KEYS.forEach(key => {
    const values = [...new Set(inboxes.map(item => `${item?.[key] ?? ""}`))];
    initial[key] = values.length === 1 ? values[0] : "";
  });
  return initial;
};

const BulkEditInboxFieldsPopup = observer(() => {
  const [formValues, setFormValues] = useState({});
  const [initialValues, setInitialValues] = useState({});

  const selectedInboxIds = connectedInboxStore?.selectedInboxIds || [];
  const selectedInboxes = (connectedInboxStore?.accountData || []).filter(item =>
    selectedInboxIds.includes(item?.id)
  );
  const selectedCount = selectedInboxes?.length || 0;
  const extraChipsCount = selectedCount - CHIP_LIMIT;

  useEffect(() => {
    if (connectedInboxStore?.showBulkEditPopup) {
      const initial = resolveInitialValues(selectedInboxes);
      setInitialValues(initial);
      setFormValues(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedInboxStore?.showBulkEditPopup]);

  const setField = (key, value) =>
    setFormValues(prev => ({ ...prev, [key]: value }));

  const handleSignatureChange = (value, source) => {
    if (source === "user") {
      setField("signature", value);
    }
  };

  const closePopup = () => {
    if (connectedInboxStore?.bulkUpdateLoader) return;
    connectedInboxStore.setShowBulkEditPopup(false);
  };

  const getChangedFields = () => {
    const fields = {};
    FIELD_KEYS.forEach(key => {
      const value = formValues?.[key];
      const isEmpty = value === undefined || value === null || `${value}`.trim() === "";
      if (!isEmpty && `${value}` !== `${initialValues?.[key] ?? ""}`) {
        fields[key] = NUMBER_FIELD_KEYS.includes(key) ? +value : value;
      }
    });
    return fields;
  };

  const changedFields = getChangedFields();
  const hasChanges = Object.keys(changedFields)?.length > 0;

  const handleSave = () => {
    if (connectedInboxStore?.bulkUpdateLoader || !hasChanges) return;
    connectedInboxStore.bulkUpdateInboxFields(changedFields);
  };

  const HeaderChildren = () => (
    <div className="commonPopupHeader bulkEditInboxFieldsHeader">
      <div className="bulkEditInboxFieldsHeaderTop">
        <div>
          <h4 className="commonPopupHeading">Edit Fields</h4>
          <p className="bulkEditInboxFieldsSubText">
            Editing{" "}
            <span className="bulkEditInboxFieldsCount">
              {selectedCount} inbox{selectedCount !== 1 ? "es" : ""}
            </span>
            . Only filled fields will be updated.
          </p>
        </div>
        <i
          className="material-icons commonPopupCloseIcon"
          onClick={closePopup}
        >
          close
        </i>
      </div>
      {selectedCount > 0 && (
        <div className="bulkEditInboxFieldsChips">
          {selectedInboxes.slice(0, CHIP_LIMIT).map(item => (
            <span className="bulkEditInboxFieldsChip" key={item?.id}>
              {getInboxEmail(item)}
            </span>
          ))}
          {extraChipsCount > 0 && (
            <span
              className="bulkEditInboxFieldsChip extraChip"
              title={selectedInboxes
                .slice(CHIP_LIMIT)
                .map(getInboxEmail)
                .join(", ")}
            >
              +{extraChipsCount} more
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    connectedInboxStore?.showBulkEditPopup && (
      <CommonPopup
        className="bulkEditInboxFieldsPopup"
        closeIconCbk={closePopup}
        HeaderChildren={HeaderChildren}
        hasHeaderChildren={true}
      >
        <div className="bulkEditInboxFieldsBody">
          <div className="bulkEditInboxFieldsRow">
            <NumberInputBox
              type="text"
              id="bulkFromName"
              name="bulkFromName"
              label="From Name"
              value={formValues?.fromName || ""}
              placeholder="Enter From Name"
              changeCbk={value => setField("fromName", value)}
              loader={false}
            />
            <NumberInputBox
              type="text"
              id="bulkSenderName"
              name="bulkSenderName"
              label="Sender Name"
              value={formValues?.senderName || ""}
              placeholder="Enter Sender Name"
              changeCbk={value => setField("senderName", value)}
              loader={false}
            />
          </div>
          <NumberInputBox
            type="text"
            id="bulkCalendarUrl"
            name="bulkCalendarUrl"
            label="Calendar URL"
            value={formValues?.calendarUrl || ""}
            placeholder="Enter Calendar URL"
            changeCbk={value => setField("calendarUrl", value)}
            loader={false}
          />
          <div className="bulkEditEmailDailyLimit">
            <h3 className="title">Email Daily Limit</h3>
            <div className="bulkEditInboxFieldsRow">
              <NumberInputBox
                type="number"
                id="bulkDailyLimit"
                name="bulkDailyLimit"
                label="Max emails per day"
                min={1}
                max={1000}
                value={formValues?.dailyLimit || ""}
                placeholder="Enter a value between 1 to 1000"
                changeCbk={value => setField("dailyLimit", value)}
                loader={false}
              />
              <NumberInputBox
                type="number"
                id="bulkInterval"
                name="bulkInterval"
                label="Min delay between emails"
                subLabel="(in seconds)"
                min={15}
                max={9999}
                value={formValues?.interval || ""}
                placeholder="Enter a value between 15 and 9999"
                changeCbk={value => setField("interval", value)}
                loader={false}
              />
            </div>
          </div>
          <div className="bulkEditInboxSignature">
            <h3 className="title">Signature</h3>
            <QuilEditor
              bodyOnChange={handleSignatureChange}
              prefilledValue={initialValues?.signature || ""}
              inboxIds={selectedInboxIds}
            />
          </div>
        </div>
        <div className="bulkEditInboxFieldsFooter">
          <p className="bulkEditInboxFieldsFooterText">
            Fields left blank won't be changed.
          </p>
          <div className="bulkEditInboxFieldsFooterActions">
            <Button
              id="bulkEditCancelBtn"
              name="bulkEditCancelBtn"
              btnText="Cancel"
              type="button"
              className="secondaryBtn"
              click={closePopup}
            />
            <Button
              id="bulkEditSaveBtn"
              name="bulkEditSaveBtn"
              btnText={`Save to ${selectedCount} inbox${selectedCount !== 1 ? "es" : ""}`}
              type="button"
              className={`blueConfirmBtn ${connectedInboxStore?.bulkUpdateLoader ? "loading" : ""}`}
              loader={connectedInboxStore?.bulkUpdateLoader}
              disabled={connectedInboxStore?.bulkUpdateLoader || !hasChanges}
              click={handleSave}
            />
          </div>
        </div>
      </CommonPopup>
    )
  );
});

export default BulkEditInboxFieldsPopup;
export { BulkEditInboxFieldsPopup };
